import { RAFFLE_EVENT_STATUS, RaffleEventStatus, RaffleGroupDetail, RepresentativeRaffleEvent } from './types';

/**
 * displayDate를 기준으로 이벤트의 동적 status를 계산합니다.
 * @param startAt 이벤트 시작 시간
 * @param endAt 이벤트 종료 시간
 * @param targetDate 기준 날짜
 * @returns 계산된 RaffleEventStatus
 */
function calculateEventStatus(
  startAt: string,
  endAt: string,
  targetDate: Date,
): RaffleEventStatus {
  const start = new Date(startAt);
  const end = new Date(endAt);

  if (targetDate < start) {
    return RAFFLE_EVENT_STATUS.SCHEDULED;
  }
  if (targetDate >= start && targetDate <= end) {
    return RAFFLE_EVENT_STATUS.IN_PROGRESS;
  }
  return RAFFLE_EVENT_STATUS.COMPLETED;
}

/**
 * RaffleEvent를 RepresentativeRaffleEvent로 변환합니다.
 * @param event RaffleEvent 객체
 * @param targetDate 기준 날짜 (오픈 시간 내인지 확인용)
 * @returns RepresentativeRaffleEvent 객체
 */
export function convertToRepresentativeRaffleEvent(
  raffleGroupDetail: RaffleGroupDetail,
  displayDate?: Date,
): RepresentativeRaffleEvent {
  const { group, events } = raffleGroupDetail;
  const targetDate = displayDate || new Date();

  // 이벤트를 시작 시간 기준으로 정렬
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
  );

  // 1. 진행중인 이벤트 찾기
  const activeEvent = sortedEvents.find((event) => {
    const startAt = new Date(event.startAt);
    const endAt = new Date(event.endAt);
    return targetDate >= startAt && targetDate <= endAt;
  });

  // 진행중인 이벤트가 있으면 활성 이미지 반환
  if (activeEvent != null) {
    const status = calculateEventStatus(activeEvent.startAt, activeEvent.endAt, targetDate);

    return {
      couponRaffleEvent: {
        id: activeEvent.id,
        groupId: activeEvent.groupId,
        startAt: activeEvent.startAt,
        endAt: activeEvent.endAt,
        status,
        state: activeEvent.state,
        imageUrl: activeEvent.openedImageUrl,
        videoUrl: activeEvent.openedVideoUrl,
        buttonText: activeEvent.buttonText,
      },
      isEnterable: status === RAFFLE_EVENT_STATUS.IN_PROGRESS,
    };
  }

  // 2. 최초 이벤트 시작 전인지 확인
  const firstEvent = sortedEvents[0];
  if (firstEvent != null && targetDate < new Date(firstEvent.startAt)) {
    const status = calculateEventStatus(firstEvent.startAt, firstEvent.endAt, targetDate);
    return {
      couponRaffleEvent: {
        id: firstEvent.id,
        groupId: firstEvent.groupId,
        startAt: firstEvent.startAt,
        endAt: firstEvent.endAt,
        status,
        state: firstEvent.state,
        imageUrl: group.teaserImageUrl,
        buttonText: firstEvent.buttonText,
      },
      isEnterable: status === RAFFLE_EVENT_STATUS.IN_PROGRESS,
    };
  }

  // 3. 종료된 이벤트 중 가장 최근 이벤트 찾기 (이벤트 사이 or 마지막 이벤트 종료 후)
  const closedEvent = sortedEvents
    .filter((event) => targetDate > new Date(event.endAt))
    .pop();

  if (closedEvent != null) {
    const status = calculateEventStatus(closedEvent.startAt, closedEvent.endAt, targetDate);

    return {
      couponRaffleEvent: {
        id: closedEvent.id,
        groupId: closedEvent.groupId,
        startAt: closedEvent.startAt,
        endAt: closedEvent.endAt,
        status,
        state: closedEvent.state,
        imageUrl: closedEvent.closedImageUrl,
        buttonText: closedEvent.buttonText,
      },
      isEnterable: status === RAFFLE_EVENT_STATUS.IN_PROGRESS,
    };
  }

  // 4. 예외 상황: 이벤트가 없는 경우 티저 이미지 반환
  return {
    couponRaffleEvent: {
      imageUrl: group.teaserImageUrl,
    },
    isEnterable: false,
  };
}
